/**
 * Browser -> BFF client. Same-origin only; cookies are host-scoped and httpOnly.
 * Every response goes through the cross-tab sign-out observer (identity contract): a 401 carrying
 * x-qm-session-state signs out every tab, purges device data and sends the user to sign-in with ?next=
 * so a deep link (QM-D19-002) returns to the item after re-auth.
 */
import type { ErrorEnvelope } from '@qm/contracts';

export class BffError extends Error {
  constructor(readonly status: number, readonly envelope: ErrorEnvelope | null, readonly sessionEnded = false) {
    super(envelope?.error.message ?? `Request failed (${status})`);
  }
}

type Observer = (res: { status: number; headers: { get(name: string): string | null } }) => boolean;
let observer: Observer | null = null;
/** Installed once by the app shell (createCrossTabSignOut(...).observe). */
export function setSessionObserver(o: Observer | null): void { observer = o; }

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export async function bff<T>(path: `/v1/${string}`, init: RequestInit = {}, fetchFn: FetchLike = (i, r) => fetch(i, r)): Promise<T> {
  const res = await fetchFn(`/bff${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: { accept: 'application/json', ...(init.body ? { 'content-type': 'application/json' } : {}), ...init.headers },
  });
  // true when this 401 already started cross-tab sign-out. The caller must not navigate again.
  const sessionEnded = observer?.(res) === true;
  // 204 is "no session" for GET /v1/identity/me (the worker rewrites an anonymous 401). Not an error.
  if (res.status === 204) return null as T;
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new BffError(res.status, body as ErrorEnvelope | null, sessionEnded);
  return (body as { data: T }).data;
}

export const bffJson = <T>(path: `/v1/${string}`, method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', body?: unknown) =>
  bff<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });
